import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ShiftObj, TakenBreakObj, WeekDay } from "store/reducers";
import { RootState } from "store/store";

import { goBackNow } from "./attendanceSlice";

export type PinResponse = {
  employee: Employee;
  employee_token: string;
  last_shift_obj?: ShiftObj;
  last_shift?: WorkedShift;
  guessed_location?: LocationSchema;
  current_location?: LocationSchema;
  ip_is_onsite: boolean;
  open_shift?: WorkedShift;
  still_valid?: boolean;
  url: string;
  on_call_rostered_shift_id: number | null;
  on_call_event_id: number | null;
};

type SubmitPinPayload = {
  username: string;
  password: string;
};

type ArchiveShiftPayload = {
  open_shift: WorkedShift;
  note: string;
};

export type ArchiveShift = {
  type: "archive_shift";
  worked_shift: WorkedShift;
};

type ClockOnPayload = {
  employee: Employee;
  location: LocationSchema | undefined | null;
  earningsRate: string;
};

export type ClockOn = {
  type: "clock_on";
  shift_obj: ShiftObj;
};

type ClockOffPayload = {
  open_shift: WorkedShift;
};

export type ClockOff = {
  type: "clock_off";
  shift_obj: ShiftObj;
  week_day: WeekDay;
};

export type ShiftActivityInsights = {
  total_event_count: number;
  event_counts: Record<string, number>;
  longest_inactivity: {
    start: string;
    end: string;
    duration_seconds: number;
  };
};

type StartBreakPayload = {
  open_shift: WorkedShift;
};

export type StartBreak = {
  type: "start_break";
  break_obj: TakenBreakObj;
};

type EndBreakPayload = {
  unfinishedBreak: TakenBreak;
};

export type EndBreak = {
  type: "end_break";
  break_obj: TakenBreakObj;
};

type StartTravelPayload = {
  employee_id: number;
  travel_to_location_id: number;
  travel_start_time: Date;
};

export type StartTravel = {
  type: "start_travel";
  travel_event: TravelEvent;
};

export type RecordOnCall = {
  type: "record_on_call";
  on_call_event: OnCallEvent;
};

export type RecordCallBack = {
  type: "record_call_back";
  call_back_event: CallBackEvent;
};

type RecordOnCallBody = {
  employee_id: number;
};

type RecordCallBackBody = {
  employee_id: number;
};

const attendanceApi = createApi({
  reducerPath: "attendanceApi",
  baseQuery: fetchBaseQuery({
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).attendance.pinResponse?.employee_token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    submitPin: builder.mutation<PinResponse, SubmitPinPayload>({
      query: (payload) => ({
        url: "/api/login/pin",
        method: "POST",
        body: payload,
      }),
    }),
    clockOn: builder.mutation<ClockOn, ClockOnPayload>({
      query: (payload) => ({
        url: "/api/worked_shift/clock_on",
        method: "POST",
        body: {
          employee_id: payload.employee.id,
          xero_EarningsRateName: payload.earningsRate,
          location_id: payload.location ? payload.location.id : null,
        },
      }),
    }),
    archiveShift: builder.mutation<ArchiveShift, ArchiveShiftPayload>({
      query: ({ open_shift, note }) => ({
        url: `/api/archive_shift/${open_shift.id}`,
        method: "POST",
        body: { note: note || undefined },
      }),
      onQueryStarted: async (_, { dispatch, getState, queryFulfilled }) => {
        const response = await queryFulfilled;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (response.data) {
          const state = getState() as RootState;
          const employee_token = state.attendance.pinResponse?.employee_token;

          // It was archived, load again to show the clock on button again.
          if (employee_token) {
            dispatch(attendanceApi.endpoints.refresh.initiate(employee_token));
          }
        }
      },
    }),
    clockOff: builder.mutation<ClockOff, ClockOffPayload>({
      query: ({ open_shift }) => ({
        url: "/api/worked_shift/clock_off",
        method: "POST",
        body: { worked_shift_id: open_shift.id },
      }),
    }),
    fetchShiftActivityInsights: builder.query<ShiftActivityInsights, string>({
      query: (workedShiftId) => ({
        url: "/api/worked_shift/activity_insights",
        method: "POST",
        body: { worked_shift_id: workedShiftId },
      }),
    }),
    startBreak: builder.mutation<StartBreak, StartBreakPayload>({
      query: ({ open_shift }) => ({
        url: "/api/taken_break/start_break",
        method: "POST",
        body: { worked_shift_id: open_shift.id },
      }),
    }),
    endBreak: builder.mutation<EndBreak, EndBreakPayload>({
      query: ({ unfinishedBreak }) => ({
        url: "/api/taken_break/end_break",
        method: "POST",
        body: { taken_break_id: unfinishedBreak.id },
      }),
    }),
    startTravel: builder.mutation<StartTravel, StartTravelPayload>({
      query: (payload) => ({
        url: "/api/start_travel",
        method: "POST",
        body: payload,
      }),
    }),
    recordOnCall: builder.mutation<RecordOnCall, RecordOnCallBody>({
      query: (body) => ({
        url: "/api/record_on_call",
        method: "POST",
        body,
      }),
    }),
    recordCallBack: builder.mutation<RecordCallBack, RecordCallBackBody>({
      query: (body) => ({
        url: "/api/record_call_back",
        method: "POST",
        body,
      }),
    }),
    refresh: builder.mutation<PinResponse, string>({
      query: (employeeToken) => ({
        url: "/api/auth/refresh",
        method: "POST",
        headers: {
          Authorization: `Bearer ${employeeToken}`,
        },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const response = await queryFulfilled;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!response.data) {
          dispatch(goBackNow());
        }
      },
    }),
  }),
});

export { attendanceApi };

export const {
  useSubmitPinMutation,
  useClockOnMutation,
  useArchiveShiftMutation,
  useClockOffMutation,
  useFetchShiftActivityInsightsQuery,
  useStartBreakMutation,
  useEndBreakMutation,
  useStartTravelMutation,
  useRecordOnCallMutation,
  useRecordCallBackMutation,
  useRefreshMutation,
} = attendanceApi;
